import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Logo from "../../../Images/logo.png";
import useStyles from "./style";
import http from "../../Common/RestAPIHandler";
import { connect } from "react-redux";
import TextField from "@material-ui/core/TextField";
import SnackbarComponent from "../../Common/Snackbar";
import { Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

const TwoFA = (props) => {
  const classes = useStyles();
  const [isInvalidToken, setIsInvalidToken] = useState(false);
  const [isSMS, setIsSMS] = useState(false);
  const [isEmail, setIsEmail] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [rememberFor30Days, setRememberFor30Days] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [tokenSent, setTokenSent] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSms = () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    
    http.Post(
      "auth/2fa/sms/send",
      {},
      (data) => {
        setSnackBarMessage(data.detail);
        setTokenSent(true);
        setSelectedMethod('sms');
        setIsSubmitting(false);
      },
      (status, error) => {
        setErrorMessage(error?.detail || "Failed to send SMS token");
        setIsSubmitting(false);
      }
    );
  };

  const handleEmail = () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    
    http.Post(
      "auth/2fa/email/send",
      {},
      (data) => {
        setSnackBarMessage(data.detail);
        setTokenSent(true);
        setSelectedMethod('email');
        setIsSubmitting(false);
      },
      (status, error) => {
        setErrorMessage(error?.detail || "Failed to send email token");
        setIsSubmitting(false);
      }
    );
  };

  const handleTokenSubmit = (token) => {
    if (!token || token.length !== 7) {
      setIsInvalidToken(true);
      return;
    }

    if (!selectedMethod) {
      setErrorMessage("Please request a verification code first");
      return;
    }

    setIsSubmitting(true);
    setIsInvalidToken(false);
    setErrorMessage(null);

    const payload = {
      token: token,
      type: selectedMethod,
      dont_show_30: rememberFor30Days
    };

    http.Post(
      "auth/2fa/verify",
      payload,
      (response) => {
        if (response.dont_show_30) {
          setSnackBarMessage("2FA verification successful! You won't be asked for 2FA again for 30 days.");
        } else {
          setSnackBarMessage("2FA verification successful!");
        }
        
        setTimeout(() => {
          props.history.replace(process.env.PUBLIC_URL + "/admin/tenants");
        }, 1500);
      },
      (status, error) => {
        setIsInvalidToken(true);
        setErrorMessage(error?.detail || "Invalid or expired verification code");
        setIsSubmitting(false);
      }
    );
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value;
    setVerificationCode(value);
    setIsInvalidToken(false);
    
    // Auto-submit when 7 digits are entered
    if (value && value.length === 7) {
      handleTokenSubmit(value);
    }
  };

  useEffect(() => {
    setIsSMS(props.user.is_sms);
    setIsEmail(props.user.is_email);
    
    // If no 2FA methods are enabled, redirect to dashboard immediately
    if (!props.user.is_sms && !props.user.is_email) {
      props.history.replace(process.env.PUBLIC_URL + "/admin/dashboard");
      return;
    }
  }, [props.history, props.user.is_email, props.user.is_sms]);

  return (
    <div className={classes.main}>
      <div className={classes.root}>
        <SnackbarComponent
          message={snackBarMessage}
          open={!!snackBarMessage}
          handleSnackBarClose={() => {setSnackBarMessage(null);}}
          severity={"success"}
        />
        {errorMessage && (
          <SnackbarComponent
            message={errorMessage}
            open={!!errorMessage}
            handleSnackBarClose={() => {setErrorMessage(null);}}
            severity={"error"}
          />
        )}
        <Grid
          container
          spacing={0}
          alignItems="center"
          justify="center"
          className={classes.container}
        >
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.centerElement}>
                <Typography variant="h6" color="inherit" noWrap>
                  <img
                    src={Logo}
                    alt="Not Available"
                    className={classes.logo}
                  />
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" color="textPrimary" align="center" gutterBottom>
                  Two-Factor Authentication
                </Typography>
                <Typography variant="body1" color="textSecondary" align="center">
                  Please verify your identity to continue
                </Typography>
              </Grid>

              {/* Show error message if no 2FA methods are available */}
              {!isSMS && !isEmail && (
                <Grid item xs={12}>
                  <Typography variant="body1" align="center" style={{color: 'red'}}>
                    No 2FA methods are configured for your account. Redirecting to dashboard...
                  </Typography>
                </Grid>
              )}

              {/* 2FA Method Selection */}
              {(isSMS || isEmail) && !tokenSent && (
                <Grid item xs={12}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Choose verification method:
                  </Typography>
                  <Grid container spacing={2}>
                    {isSMS && (
                      <Grid item xs={isEmail ? 6 : 12}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleSms}
                          disabled={isSubmitting}
                        >
                          {isSubmitting && selectedMethod === null ? 'Sending...' : 'SMS Verification'}
                        </Button>
                      </Grid>
                    )}
                    {isEmail && (
                      <Grid item xs={isSMS ? 6 : 12}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleEmail}
                          disabled={isSubmitting}
                        >
                          {isSubmitting && selectedMethod === null ? 'Sending...' : 'Email Verification'}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* Verification Code Input */}
              {tokenSent && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      id="verification-code-input"
                      label="Verification Code (7 digits)"
                      type="text"
                      margin="dense"
                      variant="outlined"
                      fullWidth
                      value={verificationCode}
                      onChange={handleVerificationCodeChange}
                      disabled={isSubmitting}
                      inputProps={{
                        maxLength: 7,
                        pattern: "[0-9]*"
                      }}
                      error={isInvalidToken}
                      helperText={isInvalidToken ? "Invalid or expired verification code" : 
                                  `Enter the 7-digit code sent via ${selectedMethod === 'sms' ? 'SMS' : 'Email'}`}
                    />
                  </Grid>

                  {/* Remember for 30 Days Option */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberFor30Days}
                          onChange={(e) => setRememberFor30Days(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Don't show again for 30 days"
                    />
                  </Grid>

                  {/* Resend Options */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
                      Didn't receive the code?
                    </Typography>
                    <Grid container spacing={2}>
                      {isSMS && (
                        <Grid item xs={isEmail ? 6 : 12}>
                          <Button
                            variant="text"
                            fullWidth
                            onClick={() => {
                              setTokenSent(false);
                              setSelectedMethod(null);
                              setVerificationCode("");
                              handleSms();
                            }}
                            disabled={isSubmitting}
                          >
                            Resend SMS
                          </Button>
                        </Grid>
                      )}
                      {isEmail && (
                        <Grid item xs={isSMS ? 6 : 12}>
                          <Button
                            variant="text"
                            fullWidth
                            onClick={() => {
                              setTokenSent(false);
                              setSelectedMethod(null);
                              setVerificationCode("");
                              handleEmail();
                            }}
                            disabled={isSubmitting}
                          >
                            Resend Email
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Manual Submit Button */}
                  <Grid item xs={12} className={classes.centerElement}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleTokenSubmit(verificationCode)}
                      disabled={isSubmitting || verificationCode.length !== 7}
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify Code'}
                    </Button>
                  </Grid>
                </>
              )}

              <Grid item xs={12} className={classes.centerElement}>
                <Link href="/admin/login">Return to login page.</Link>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user.user,
  };
};
export default connect(mapStateToProps)(TwoFA);